import { useCallback, useEffect, useRef, useState } from "react";
import { safeLocal } from "../lib/safeStorage";
import { TTS_ENABLED_KEY } from "../lib/storageKeys";
import { useDemoMode } from "../context/DemoModeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Persisted voice preference (one of the OpenAI TTS voice names).
const TTS_VOICE_KEY = "trubeaute.mirror.tts.voice";
const DEFAULT_VOICE = "nova";

/**
 * useTTS — warm AI-generated voice for the AR mirror coach.
 *
 * Primary path: OpenAI TTS (model `tts-1`, voice `nova` by default)
 * generated server-side via `POST /api/tts`. Audio is returned as
 * `audio/mpeg` and played through an `HTMLAudioElement`. The backend
 * caches identical text→audio pairs aggressively, so repeated lines
 * are essentially free.
 *
 * Fallback: browser SpeechSynthesis. Used only when the backend can't
 * be reached (offline, network hiccup) so a user is never stranded
 * silent. The UI doesn't need to know which path served a given line.
 *
 * Public surface (unchanged from the prior browser-only impl):
 *   { enabled, toggle, speak, stop, supported, voice, setVoice }
 */
export function useTTS({ persist = true, remote = true } = {}) {
  const supported =
    typeof window !== "undefined" &&
    (typeof window.Audio === "function" || "speechSynthesis" in window);

  const [enabled, setEnabled] = useState(
    () => persist && safeLocal.getItem(TTS_ENABLED_KEY) === "1",
  );
  const [voice, setVoiceState] = useState(
    () => (persist ? safeLocal.getItem(TTS_VOICE_KEY) : null) || DEFAULT_VOICE,
  );

  // The currently-playing audio element (or pending fetch). We hold a
  // ref so a fast Next-Next-Next doesn't queue a backlog of audio.
  const audioRef = useRef(null);
  const pendingAbortRef = useRef(null);

  // Whether TTS is actively playing. Voice-command listeners use this
  // to gate themselves so the mic doesn't accidentally hear Trouvé say
  // "next" mid-instruction and trigger an unwanted advance.
  const [speaking, setSpeaking] = useState(false);

  // Per-component in-memory blob URL cache so we don't re-decode the
  // same audio on every advance. The backend already caches the bytes;
  // this just avoids wrapping them in a new Blob URL each time.
  const blobCacheRef = useRef(new Map());

  // Persist toggle + voice preferences across sessions.
  useEffect(() => {
    if (!persist) return;
    safeLocal.setItem(TTS_ENABLED_KEY, enabled ? "1" : "0");
  }, [enabled, persist]);
  useEffect(() => {
    if (!persist) return;
    safeLocal.setItem(TTS_VOICE_KEY, voice || DEFAULT_VOICE);
  }, [voice, persist]);

  const _killCurrent = useCallback(() => {
    // 1. Abort any pending fetch. 2. Stop any playing audio.
    // 3. Cancel browser speech if we ever fell back to it.
    try {
      pendingAbortRef.current?.abort();
    } catch {
      /* no-op */
    }
    pendingAbortRef.current = null;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
      } catch {
        /* no-op */
      }
      audioRef.current = null;
    }
    setSpeaking(false);
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* no-op */
      }
    }
  }, []);

  // Stop is just `_killCurrent` exposed.
  const stop = _killCurrent;

  const _browserFallback = useCallback(
    (text) => {
      if (typeof window === "undefined" || !("speechSynthesis" in window))
        return;
      try {
        window.speechSynthesis.cancel();
        const utter = new window.SpeechSynthesisUtterance(text);
        utter.rate = 0.92;
        utter.pitch = 1.0;
        window.speechSynthesis.speak(utter);
      } catch {
        /* swallow — fallback is best-effort */
      }
    },
    [],
  );

  const speak = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!enabled || !trimmed || !supported) return;
      _killCurrent();

      if (!remote) {
        _browserFallback(trimmed);
        return;
      }

      const cacheKey = `${voice}::${trimmed}`;
      let url = blobCacheRef.current.get(cacheKey);

      if (!url) {
        const controller = new AbortController();
        pendingAbortRef.current = controller;
        try {
          const resp = await fetch(`${API}/tts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              text: trimmed,
              voice: voice || DEFAULT_VOICE,
              model: "tts-1",
              speed: 1.0,
            }),
            credentials: "include",
            signal: controller.signal,
          });
          if (!resp.ok) {
            throw new Error(`TTS HTTP ${resp.status}`);
          }
          const blob = await resp.blob();
          url = URL.createObjectURL(blob);
          // Bound cache so we don't leak object URLs forever.
          if (blobCacheRef.current.size >= 50) {
            const oldestKey = blobCacheRef.current.keys().next().value;
            const oldUrl = blobCacheRef.current.get(oldestKey);
            try {
              URL.revokeObjectURL(oldUrl);
            } catch {
              /* no-op */
            }
            blobCacheRef.current.delete(oldestKey);
          }
          blobCacheRef.current.set(cacheKey, url);
        } catch (e) {
          if (e?.name === "AbortError") return;
          // eslint-disable-next-line no-console
          console.warn("[tts] backend failed, falling back to browser TTS:", e);
          _browserFallback(trimmed);
          return;
        } finally {
          if (pendingAbortRef.current === controller) {
            pendingAbortRef.current = null;
          }
        }
      }

      try {
        const audio = new Audio(url);
        audio.preload = "auto";
        audio.onplay = () => setSpeaking(true);
        audio.onended = () => setSpeaking(false);
        audio.onpause = () => setSpeaking(false);
        audio.onerror = () => setSpeaking(false);
        audioRef.current = audio;
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === "function") {
          // Autoplay can fail on iOS until first user gesture. Fall back
          // silently to browser TTS in that case.
          playPromise.catch(() => _browserFallback(trimmed));
        }
      } catch {
        _browserFallback(trimmed);
      }
    },
    [enabled, supported, voice, _killCurrent, _browserFallback, remote],
  );

  // Background-load + cache the audio for `text` WITHOUT playing it.
  // This is the core fix for the "delay between steps" complaint —
  // LiveCoach calls prefetch() for step N+1 as soon as step N starts
  // speaking, so by the time the user advances, the audio is already
  // in the blob cache and `speak()` plays it instantly with zero
  // network round-trip.
  //
  // Best-effort:
  //   • No-op if already cached (re-prefetching is harmless but wasteful).
  //   • No-op if TTS is disabled or unsupported.
  //   • Silent on failure — the eventual `speak()` will re-attempt
  //     normally if the prefetch errored.
  //   • Does NOT abort any in-flight `speak()` call — prefetches run
  //     in parallel with playback.
  const prefetch = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!remote || !enabled || !trimmed || !supported) return;
      const cacheKey = `${voice}::${trimmed}`;
      if (blobCacheRef.current.has(cacheKey)) return; // already warm
      try {
        const resp = await fetch(`${API}/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: trimmed,
            voice: voice || DEFAULT_VOICE,
            model: "tts-1",
            speed: 1.0,
          }),
          credentials: "include",
        });
        if (!resp.ok) return;
        const blob = await resp.blob();
        // Re-check the cache after the await — `setVoice()` may have
        // cleared it while we were fetching, in which case we'd be
        // caching audio for the wrong voice. Also, another speak() may
        // have already inserted this key.
        if (blobCacheRef.current.has(cacheKey)) return;
        const url = URL.createObjectURL(blob);
        if (blobCacheRef.current.size >= 50) {
          const oldestKey = blobCacheRef.current.keys().next().value;
          const oldUrl = blobCacheRef.current.get(oldestKey);
          try {
            URL.revokeObjectURL(oldUrl);
          } catch {
            /* no-op */
          }
          blobCacheRef.current.delete(oldestKey);
        }
        blobCacheRef.current.set(cacheKey, url);
      } catch {
        // swallow — `speak()` will retry on real demand
      }
    },
    [enabled, supported, voice, remote],
  );

  const toggle = useCallback(() => {
    setEnabled((cur) => {
      const next = !cur;
      if (!next) _killCurrent();
      return next;
    });
  }, [_killCurrent]);

  const setVoice = useCallback(
    (next) => {
      // Switching voice invalidates the audio cache (different audio
      // for the same text). We also stop anything mid-sentence.
      _killCurrent();
      blobCacheRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* no-op */
        }
      });
      blobCacheRef.current.clear();
      setVoiceState(next || DEFAULT_VOICE);
    },
    [_killCurrent],
  );

  // Cleanup on unmount: cancel anything in flight + revoke blob URLs.
  useEffect(() => {
    const cache = blobCacheRef.current;
    return () => {
      _killCurrent();
      cache.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* no-op */
        }
      });
      cache.clear();
    };
  }, [_killCurrent]);

  // Demo mode forces TTS on regardless of the user's saved preference
  // — investors / press should always hear Trouvé during a demo. The
  // override is read-only (we don't persist) so the user's pref returns
  // the moment demo mode is turned off / the tab closes.
  const { active: demoActive } = useDemoMode();
  const effectiveEnabled = enabled || demoActive;

  return { enabled: effectiveEnabled, toggle, speak, prefetch, stop, supported, voice, setVoice, speaking };
}
